import React, { useState, useEffect, useCallback, useRef } from 'react'
import Select from 'react-select'
import { debounce } from 'lodash'
import { Package, Globe, Building2 } from 'lucide-react'
import useStore from '../store/useStore'
import { cachedApiService } from '../services/api'

const FILTER_CONFIG = [
  { key: 'produits', label: 'Produits', icon: Package, color: 'blue', placeholder: 'Rechercher des produits...' },
  { key: 'fournisseurs', label: 'Fournisseurs', icon: Globe, color: 'emerald', placeholder: 'Rechercher des fournisseurs...' },
  { key: 'importateurs', label: 'Importateurs', icon: Building2, color: 'violet', placeholder: 'Rechercher des importateurs...' },
]

const MainFilters = ({ layout = 'horizontal' }) => {
  const { filters, filterOptions, updateFilter, setFilterOptions } = useStore()
  const [loadingOptions, setLoadingOptions] = useState({
    produits: false,
    fournisseurs: false,
    importateurs: false,
  })
  
  const [searchInputs, setSearchInputs] = useState({
    produits: '',
    fournisseurs: '',
    importateurs: ''
  })

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '38px',
      borderColor: state.isFocused ? '#3b82f6' : 'var(--border-color, #e5e7eb)',
      backgroundColor: 'var(--select-bg, white)',
      boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
      borderRadius: '0.5rem',
      '&:hover': { borderColor: '#93c5fd' }
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: 'var(--tag-bg, #eff6ff)',
      borderRadius: '6px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'var(--tag-color, #1e40af)',
      fontSize: '13px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: 'var(--tag-color, #1e40af)',
      '&:hover': { backgroundColor: 'var(--tag-hover, #dbeafe)', color: 'var(--tag-color, #1e40af)' },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
      fontSize: '13px',
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '13px',
      backgroundColor: state.isSelected ? '#dbeafe' : state.isFocused ? '#f3f4f6' : 'transparent',
      color: state.isSelected ? '#1e40af' : 'inherit',
      fontWeight: state.isSelected ? '600' : '400',
      cursor: 'pointer',
      paddingLeft: state.isSelected ? '32px' : '12px',
      position: 'relative',
      '&:before': state.isSelected ? {
        content: '"✓"',
        position: 'absolute',
        left: '12px',
        color: '#2563eb',
        fontWeight: 'bold',
      } : {}
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '0.75rem',
      overflow: 'hidden',
      boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
    }),
  }

  const formatOptionLabel = ({ label, count }) => (
    <div className="flex justify-between items-center w-full">
      <span className="truncate">{label}</span>
      {count !== undefined && (
        <span className="text-[11px] text-gray-400 dark:text-gray-500 ml-2 flex-shrink-0 tabular-nums">
          {count.toLocaleString()}
        </span>
      )}
    </div>
  )

  const buildCurrentFilters = useCallback(() => {
    return {
      produits: filters.produits || [],
      fournisseurs: filters.fournisseurs || [],
      importateurs: filters.importateurs || [],
      nif_importateur: filters.nif_importateur || [],
      pays_origine: filters.pays_origine || [],
      incoterm: filters.incoterm || [],
      code_regime_apc: filters.code_regime_apc || [],
      date_debut: filters.date_debut || null,
      date_fin: filters.date_fin || null,
      annee_declaration: filters.annee_declaration || []
    }
  }, [filters])

  const debouncedSearch = useCallback(
    debounce(async (filterType, query) => {
      setLoadingOptions(prev => ({ ...prev, [filterType]: true }))
      
      try {
        const currentStoreFilters = useStore.getState().filters
        const currentFilters = {
          produits: currentStoreFilters.produits || [],
          fournisseurs: currentStoreFilters.fournisseurs || [],
          importateurs: currentStoreFilters.importateurs || [],
          nif_importateur: currentStoreFilters.nif_importateur || [],
          pays_origine: currentStoreFilters.pays_origine || [],
          incoterm: currentStoreFilters.incoterm || [],
          code_regime_apc: currentStoreFilters.code_regime_apc || [],
          date_debut: currentStoreFilters.date_debut || null,
          date_fin: currentStoreFilters.date_fin || null,
          annee_declaration: currentStoreFilters.annee_declaration || []
        }
        
        const options = await cachedApiService.getFilterOptions(filterType, query, currentFilters)
        if (options && options.options && Array.isArray(options.options)) {
          const validOptions = options.options.filter(option => 
            option && typeof option === 'object' &&
            typeof option.value === 'string' && typeof option.label === 'string' &&
            option.value.trim() !== '' && option.label.trim() !== ''
          )
          setFilterOptions(filterType, validOptions)
        } else {
          setFilterOptions(filterType, [])
        }
      } catch (error) {
        console.error(`Erreur lors du chargement des options ${filterType}:`, error)
        setFilterOptions(filterType, [])
      } finally {
        setLoadingOptions(prev => ({ ...prev, [filterType]: false }))
      }
    }, 300),
    []
  )

  const handleDropdownOpen = useCallback((filterType) => {
    const currentStoreFilters = useStore.getState().filters
    const currentFilters = {
      produits: currentStoreFilters.produits || [],
      fournisseurs: currentStoreFilters.fournisseurs || [],
      importateurs: currentStoreFilters.importateurs || [],
      nif_importateur: currentStoreFilters.nif_importateur || [],
      pays_origine: currentStoreFilters.pays_origine || [],
      incoterm: currentStoreFilters.incoterm || [],
      code_regime_apc: currentStoreFilters.code_regime_apc || [],
      date_debut: currentStoreFilters.date_debut || null,
      date_fin: currentStoreFilters.date_fin || null,
      annee_declaration: currentStoreFilters.annee_declaration || []
    }
    const contextKey = JSON.stringify(currentFilters)
    const lastContextKey = JSON.stringify(lastContextFilters.current?.[filterType] || {})
    
    if (!filterOptions[filterType] || filterOptions[filterType].length === 0 || contextKey !== lastContextKey) {
      lastContextFilters.current = lastContextFilters.current || {}
      lastContextFilters.current[filterType] = currentFilters
      debouncedSearch(filterType, '')
    }
  }, [debouncedSearch, filterOptions])
  
  const lastContextFilters = useRef({})
  
  useEffect(() => {
    const filterTypes = ['produits', 'fournisseurs', 'importateurs']
    
    filterTypes.forEach(filterType => {
      const currentFilters = buildCurrentFilters()
      const contextKey = JSON.stringify(currentFilters)
      const lastContextKey = JSON.stringify(lastContextFilters.current?.[filterType] || {})
      
      const hasActiveFilters = Object.values(currentFilters).some(filter => 
        Array.isArray(filter) ? filter.length > 0 : filter !== null
      )
      
      if (contextKey !== lastContextKey && hasActiveFilters) {
        lastContextFilters.current = lastContextFilters.current || {}
        lastContextFilters.current[filterType] = currentFilters
        const currentSearchText = searchInputs[filterType] || ''
        debouncedSearch(filterType, currentSearchText)
      }
    })
  }, [filters, debouncedSearch, searchInputs])

  const [selectedOptionsCache, setSelectedOptionsCache] = useState({
    produits: [],
    fournisseurs: [],
    importateurs: []
  })

  const handleSelectChange = (filterName, selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map(option => option.value) : []
    setSelectedOptionsCache(prev => ({
      ...prev,
      [filterName]: selectedOptions || []
    }))
    updateFilter(filterName, values)
  }
  
  const getDisplayedValue = (filterType) => {
    const matchedOptions = (Array.isArray(filterOptions[filterType]) ? filterOptions[filterType] : []).filter(option => 
      option && option.value && filters[filterType]?.includes(option.value)
    )
    if (matchedOptions.length > 0) return matchedOptions
    return (selectedOptionsCache[filterType] || []).filter(option =>
      filters[filterType]?.includes(option.value)
    )
  }

  const isVertical = layout === 'vertical'

  return (
    <div className="card-sm">
      <div className={`grid field-gap ${isVertical ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-3'}`}>
        {FILTER_CONFIG.map(({ key, label, icon: Icon, placeholder }) => (
          <div key={key}>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
              <Icon className="w-3.5 h-3.5" />
              {label}
              {(filters[key]?.length > 0) && (
                <span className="ml-auto bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {filters[key].length}
                </span>
              )}
            </label>
            <Select
              isMulti
              value={getDisplayedValue(key)}
              options={Array.isArray(filterOptions[key]) ? filterOptions[key] : []}
              inputValue={searchInputs[key]}
              onChange={(selected) => {
                try { handleSelectChange(key, selected || []) }
                catch (error) { console.error(`Erreur onChange ${key}:`, error) }
              }}
              onInputChange={(query, { action }) => {
                try {
                  if (typeof query === 'string') {
                    if (action === 'input-change') {
                      setSearchInputs(prev => ({ ...prev, [key]: query }))
                      debouncedSearch(key, query)
                    }
                    if (action === 'set-value' || action === 'menu-close') return
                  }
                } catch (error) { console.error(`Erreur onInputChange ${key}:`, error) }
              }}
              onFocus={() => handleDropdownOpen(key)}
              onMenuOpen={() => handleDropdownOpen(key)}
              placeholder={loadingOptions[key] ? "Chargement..." : placeholder}
              noOptionsMessage={({ inputValue }) => 
                inputValue ? `Aucun résultat pour "${inputValue}"` : "Aucune option disponible"
              }
              loadingMessage={() => "Recherche..."}
              isLoading={loadingOptions[key]}
              styles={customSelectStyles}
              formatOptionLabel={formatOptionLabel}
              className="react-select-container"
              classNamePrefix="react-select"
              menuShouldScrollIntoView={false}
              maxMenuHeight={250}
              isClearable={false}
              isSearchable={true}
              filterOption={null}
              closeMenuOnSelect={false}
              blurInputOnSelect={false}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

export default MainFilters
